import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Layer, Marker, Source, useMap } from 'react-map-gl';
import { createMapSourceAtom } from 'components/map/atoms';
import { useOverlapPulse } from './use-overlap-pulse';
import {
  type TcSelectedDevArea,
  tcSelectedDevAreaAtom,
  tcSelectDevAreaRequestAtom,
  tcEnabledUnitFiltersAtom,
  tcFilteredUnitsAtom,
  clonedUnitsAtom,
  tcOverlappingUnitsAtom,
  removedUnitIdsAtom,
  unitAzimuthOverridesAtom,
  tcUnitNameColorMapAtom,
  tcUnitResolutionRefreshAtom,
  tcHasTypeCurveAreaAtom,
  tcInventoryFoucsGateAtom,
  tcInventoryGunBarrelScopeAtom,
  tcHighlightedFormationAtom,
  tcSetbackZoneIdAtom,
} from './type-curve-filters/atoms';
import {
  effectiveMapFilterModeAtoms,
  mapBoundsAtoms,
  aoiPolygonAtoms,
  aoiDrawingAtoms,
  mapGesturesSuspendedAtoms,
  unitShapeWorkingRingAtoms,
} from './inventory-surface/map-atoms';
// Layering note: the MBR maths this needs already lives with the quarter grid,
// so it is imported rather than duplicated here.
import { ringAxisAzimuthRad } from 'pages/inventory-workspace/unit-builder/quarter-grid';
import { axisDelta, azimuthOverrideKey } from './azimuth-override';
import { useSurface } from './inventory-surface/surface';
import { builderSectionCrossAtom } from './inventory-surface/atoms';
import { geometryRefreshAtom, typeCurveUnitsVisibilityAtom } from './atoms';
import {
  activeTcWorkspaceAtom,
  tcDetailsRightPanelAtom,
} from 'components/type-curve-workspace/atoms';
import { buildDevAreasTileFilterParams } from './build-development-areas-payload';
import mapboxGl, { type Expression } from 'mapbox-gl';
import request from 'lib/request';

const devAreasSourceAtom = createMapSourceAtom('developmentAreas');

const NONE_FILTER: any[] = ['==', 'name', ''];

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

// Trailing-edge delay before a reshape commits new section-line endpoints. The
// shape editor reports every drag move; this collapses a gesture into one commit
// so the gun barrel and slot table fetch once, at the end.
const SHAPE_EDIT_COMMIT_DEBOUNCE_MS = 300;

const FT_PER_DEG_LAT = 5280 / 0.01449; // ≈ 364,389 ft per degree latitude

// ─── Axis frame for drag support ───

interface DevAreaFrame {
  centerLat: number;
  centerLon: number;
  azimuthRad: number;
  uMin: number; // min projection along the long axis (ft from center)
  uMax: number; // max projection along the long axis (ft from center)
  vMin: number; // min projection along the perpendicular axis (ft)
  vMax: number; // max projection along the perpendicular axis (ft)
  ftPerDegLon: number;
}

// Cached per-unit bounds + frame derived from tiles, so off-screen units stay
// navigable without re-querying.
interface CachedUnit {
  bounds: mapboxGl.LngLatBounds;
  props: Record<string, unknown>;
  frame: DevAreaFrame | null;
}

/**
 * Compute section-line endpoints + midpoint for a given position `t ∈ [0,1]` along the long axis.
 * @param {DevAreaFrame} frame - The development area frame
 * @param {number} t - Position along the long axis (0 to 1)
 * @return {object} Start/end coords and midpoint
 */
function sectionLineAtT(frame: DevAreaFrame, t: number) {
  const {
    centerLat,
    centerLon,
    azimuthRad,
    uMin,
    uMax,
    vMin,
    vMax,
    ftPerDegLon,
  } = frame;

  // Offset along the long axis — t maps linearly from uMin to uMax
  const u = uMin + (uMax - uMin) * t;
  const midLat = centerLat + (u * Math.cos(azimuthRad)) / FT_PER_DEG_LAT;
  const midLon = centerLon + (u * Math.sin(azimuthRad)) / ftPerDegLon;

  // Perpendicular direction (azimuth + 90°)
  // Extend the section line by 50% of the unit width on each side so it
  // clearly overshoots the polygon (matches mock-ui MapPanel behaviour).
  const perpRad = azimuthRad + Math.PI / 2;
  const halfExt = (vMax - vMin) * 0.5;
  const vStart = vMin - halfExt;
  const vEnd = vMax + halfExt;
  const startLat = midLat + (vStart * Math.cos(perpRad)) / FT_PER_DEG_LAT;
  const startLon = midLon + (vStart * Math.sin(perpRad)) / ftPerDegLon;
  const endLat = midLat + (vEnd * Math.cos(perpRad)) / FT_PER_DEG_LAT;
  const endLon = midLon + (vEnd * Math.sin(perpRad)) / ftPerDegLon;

  return {
    start: { lat: startLat, lon: startLon },
    end: { lat: endLat, lon: endLon },
    midLat,
    midLon,
  };
}

/**
 * Project a mouse lat/lon onto the long axis and return the clamped `t` value.
 * @param {DevAreaFrame} frame - The development area frame
 * @param {number} lat - Mouse latitude
 * @param {number} lon - Mouse longitude
 * @return {number} Clamped t value (0 to 1)
 */
function projectToT(frame: DevAreaFrame, lat: number, lon: number): number {
  const { centerLat, centerLon, azimuthRad, uMin, uMax, ftPerDegLon } = frame;
  if (uMax <= uMin) return 0.5;

  const yFt = (lat - centerLat) * FT_PER_DEG_LAT;
  const xFt = (lon - centerLon) * ftPerDegLon;

  // Dot-product onto the long-axis unit vector
  const u = xFt * Math.sin(azimuthRad) + yFt * Math.cos(azimuthRad);

  return Math.max(0, Math.min(1, (u - uMin) / (uMax - uMin)));
}

/**
 * Return a copy of the frame with its long-axis azimuth rotated by `deg`.
 * Story 3: the cross-section line ("LTD geometry direction") can be rotated via
 * a unit override. Rotating the axis also changes which polygon extent bounds
 * the slide travel (uMin/uMax) vs the perpendicular width (vMin/vMax) — so we
 * recompute those too, otherwise the line keeps the original width and can be
 * dragged outside the unit. For right-angle rotations the extents map exactly
 * (a 90° turn swaps u↔v); other angles can't be derived from the ranges alone
 * (they'd need the raw vertices) so they fall back to azimuth-only — not used
 * today, since the rotate action is always 90°.
 * Rendering and drag-projection must use the SAME frame so the draggable handle
 * stays under the cursor. Returns the base frame unchanged when deg is 0.
 * @param {DevAreaFrame} frame - The base (auto-detected) frame.
 * @param {number} deg - Rotation in degrees (0 means no rotation).
 * @return {DevAreaFrame} Rotated frame, or the base frame when deg is 0.
 */
function rotatedFrame(frame: DevAreaFrame, deg: number): DevAreaFrame {
  if (!deg) return frame;
  const azimuthRad = frame.azimuthRad + (deg * Math.PI) / 180;
  const { uMin, uMax, vMin, vMax } = frame;
  const norm = ((deg % 360) + 360) % 360;
  if (norm === 90) {
    return {
      ...frame,
      azimuthRad,
      uMin: -vMax,
      uMax: -vMin,
      vMin: uMin,
      vMax: uMax,
    };
  }
  if (norm === 270) {
    return {
      ...frame,
      azimuthRad,
      uMin: vMin,
      uMax: vMax,
      vMin: -uMax,
      vMax: -uMin,
    };
  }
  if (norm === 180) {
    return {
      ...frame,
      azimuthRad,
      uMin: -uMax,
      uMax: -uMin,
      vMin: -vMax,
      vMax: -vMin,
    };
  }
  return { ...frame, azimuthRad };
}

// ─── Helpers ───

/**
 * Iterate all [lon, lat] coordinates from a set of Mapbox features.
 * @param {mapboxGl.MapboxGeoJSONFeature[]} features - Mapbox GeoJSON features to extract coords from.
 */
function* featureCoords(
  features: mapboxGl.MapboxGeoJSONFeature[]
): Generator<[number, number]> {
  for (const f of features) {
    const geom = f.geometry;
    if (geom.type === 'Polygon') {
      for (const ring of geom.coordinates) {
        for (const coord of ring) yield coord as [number, number];
      }
    } else if (geom.type === 'MultiPolygon') {
      for (const polygon of geom.coordinates) {
        for (const ring of polygon) {
          for (const coord of ring) yield coord as [number, number];
        }
      }
    }
  }
}

/**
 * Compute the axis frame from tile feature vertices. Uses the bounding-box
 * center as the origin (reliable for tile-clipped features — mean-of-vertices
 * is skewed by extra clip-boundary vertices MVT tiles inject). Then projects
 * all vertices onto the azimuth axis to get accurate uMin/uMax/vMin/vMax.
 * @param {mapboxGl.MapboxGeoJSONFeature[]} features - Polygon features from the vector tile source.
 * @param {number} azimuthRad - Long-axis azimuth in radians.
 * @return {DevAreaFrame | null} Frame object or null if bounds are empty.
 */
function computeAxisFrameFromFeatures(
  features: mapboxGl.MapboxGeoJSONFeature[],
  azimuthRad: number
): DevAreaFrame | null {
  // Use bounding-box center — reliable even with tile-clipped features.
  // Mean-of-vertices is skewed because tile clipping injects extra vertices
  // along tile boundaries, biasing the average.
  const bounds = computeBoundsFromFeatures(features);
  if (bounds.isEmpty()) return null;

  return computeAxisFrame(bounds, featureCoords(features), azimuthRad);
}

/**
 * The same frame, computed from an explicit ring rather than tile features —
 * used while the shape editor is running, so the cross-section line tracks the
 * boundary the user is dragging instead of the shape it had at selection.
 * @param {GeoJSON.Position[]} ring - Closed `[lon, lat]` ring.
 * @param {number} azimuthRad - Long-axis azimuth in radians.
 * @return {DevAreaFrame | null} Frame object, or null for a degenerate ring.
 */
function computeAxisFrameFromRing(
  ring: GeoJSON.Position[],
  azimuthRad: number
): DevAreaFrame | null {
  if (ring.length < 3) return null;

  const bounds = new mapboxGl.LngLatBounds();
  for (const [lon, lat] of ring) bounds.extend([lon, lat]);
  if (bounds.isEmpty()) return null;

  const coords = ring.map(([lon, lat]) => [lon, lat] as [number, number]);
  return computeAxisFrame(bounds, coords, azimuthRad);
}

/**
 * Project a set of `[lon, lat]` coordinates onto the long (u) and perpendicular
 * (v) axes of `azimuthRad`, anchored at the bounding-box center.
 * @param {mapboxGl.LngLatBounds} bounds - Bounds enclosing the coordinates.
 * @param {Iterable} coords - The `[lon, lat]` coordinates to project.
 * @param {number} azimuthRad - Long-axis azimuth in radians.
 * @return {DevAreaFrame | null} Frame object or null if the extent is degenerate.
 */
function computeAxisFrame(
  bounds: mapboxGl.LngLatBounds,
  coords: Iterable<[number, number]>,
  azimuthRad: number
): DevAreaFrame | null {
  const center = bounds.getCenter();
  const cLat = center.lat;
  const cLon = center.lng;
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos((cLat * Math.PI) / 180);

  // Project all vertices onto the long (u) and perpendicular (v) axes
  let uMin = Infinity,
    uMax = -Infinity,
    vMin = Infinity,
    vMax = -Infinity;

  for (const [lon, lat] of coords) {
    const xFt = (lon - cLon) * ftPerDegLon;
    const yFt = (lat - cLat) * FT_PER_DEG_LAT;
    const u = xFt * Math.sin(azimuthRad) + yFt * Math.cos(azimuthRad);
    const v = -xFt * Math.cos(azimuthRad) + yFt * Math.sin(azimuthRad);
    if (u < uMin) uMin = u;
    if (u > uMax) uMax = u;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  }

  if (!Number.isFinite(uMin) || uMax <= uMin) return null;

  return {
    centerLat: cLat,
    centerLon: cLon,
    azimuthRad,
    uMin,
    uMax,
    vMin,
    vMax,
    ftPerDegLon,
  };
}

/**
 * Build a DevAreaFrame synthetically from centroid + azimuth + length + width.
 * No polygon vertices needed — the unit is modeled as a 2D rectangle.
 * @param {number} centroidLat - Centroid latitude in degrees.
 * @param {number} centroidLon - Centroid longitude in degrees.
 * @param {number} azimuthDeg - Long-axis azimuth in degrees.
 * @param {number} lengthFt - Unit length in feet.
 * @param {number} widthFt - Unit width in feet.
 * @return {DevAreaFrame} Computed DevAreaFrame.
 */
function computeFrameFromUnitData(
  centroidLat: number,
  centroidLon: number,
  azimuthDeg: number,
  lengthFt: number,
  widthFt: number
): DevAreaFrame {
  const azimuthRad = (azimuthDeg * Math.PI) / 180;
  const ftPerDegLon_ = FT_PER_DEG_LAT * Math.cos((centroidLat * Math.PI) / 180);
  return {
    centerLat: centroidLat,
    centerLon: centroidLon,
    azimuthRad,
    uMin: -lengthFt / 2,
    uMax: lengthFt / 2,
    vMin: -widthFt / 2,
    vMax: widthFt / 2,
    ftPerDegLon: ftPerDegLon_,
  };
}

/**
 * Build LngLatBounds from centroid + azimuth + length + width by computing
 * the 4 corners of the rotated rectangle.
 * @param {number} centroidLat - Centroid latitude in degrees.
 * @param {number} centroidLon - Centroid longitude in degrees.
 * @param {number} azimuthDeg - Long-axis azimuth in degrees.
 * @param {number} lengthFt - Unit length in feet.
 * @param {number} widthFt - Unit width in feet.
 * @return {mapboxGl.LngLatBounds} LngLatBounds enclosing the rotated rectangle.
 */
function buildBoundsFromUnitData(
  centroidLat: number,
  centroidLon: number,
  azimuthDeg: number,
  lengthFt: number,
  widthFt: number
): mapboxGl.LngLatBounds {
  const azRad = (azimuthDeg * Math.PI) / 180;
  const ftLon = FT_PER_DEG_LAT * Math.cos((centroidLat * Math.PI) / 180);
  const halfU = lengthFt / 2;
  const halfV = widthFt / 2;
  const corners: [number, number][] = [];
  for (const [uSign, vSign] of [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const) {
    const u = uSign * halfU;
    const v = vSign * halfV;
    const xFt = u * Math.sin(azRad) - v * Math.cos(azRad);
    const yFt = u * Math.cos(azRad) + v * Math.sin(azRad);
    corners.push([
      centroidLon + xFt / ftLon,
      centroidLat + yFt / FT_PER_DEG_LAT,
    ]);
  }
  const bounds = new mapboxGl.LngLatBounds();
  for (const c of corners) bounds.extend(c);
  return bounds;
}

/**
 * The unit's four corners as a closed `[lon, lat]` ring, in order.
 *
 * `buildBoundsFromUnitData` computes the same rotated rectangle but collapses
 * it into an axis-aligned LngLatBounds; the shape editor needs the corners
 * themselves as a seed. Tile geometry can't be used for that — it is clipped at
 * tile edges (see `selectDevArea`) — and derived units have no server geometry,
 * so the frame is the only complete description of a unit's footprint.
 *
 * @param {DevAreaFrame} frame - The development area frame.
 * @return {GeoJSON.Position[]} Closed ring of the unit's corners.
 */
function buildRingFromFrame(frame: DevAreaFrame): GeoJSON.Position[] {
  const { centerLat, centerLon, azimuthRad, uMin, uMax, vMin, vMax } = frame;
  const ftLon = frame.ftPerDegLon;

  const corners: GeoJSON.Position[] = (
    [
      [uMin, vMin],
      [uMax, vMin],
      [uMax, vMax],
      [uMin, vMax],
    ] as const
  ).map(([u, v]) => {
    const xFt = u * Math.sin(azimuthRad) - v * Math.cos(azimuthRad);
    const yFt = u * Math.cos(azimuthRad) + v * Math.sin(azimuthRad);
    return [centerLon + xFt / ftLon, centerLat + yFt / FT_PER_DEG_LAT];
  });

  return [...corners, corners[0]];
}

function computeBoundsFromFeatures(
  features: mapboxGl.MapboxGeoJSONFeature[]
): mapboxGl.LngLatBounds {
  const bounds = new mapboxGl.LngLatBounds();
  for (const f of features) {
    const geom = f.geometry;
    if (geom.type === 'Polygon') {
      for (const ring of geom.coordinates) {
        for (const coord of ring) {
          bounds.extend(coord as [number, number]);
        }
      }
    } else if (geom.type === 'MultiPolygon') {
      for (const polygon of geom.coordinates) {
        for (const ring of polygon) {
          for (const coord of ring) {
            bounds.extend(coord as [number, number]);
          }
        }
      }
    }
  }
  return bounds;
}

// ─── Color by Attribute helper ───

export function buildUnitColorExpr(
  unitNameColorMap: Record<string, string>,
  defaultColor: string
): string | Expression {
  const entries = Object.entries(unitNameColorMap);
  if (entries.length === 0) return defaultColor;

  // Group unit names by color for a compact match expression.
  const colorGroups = new Map<string, string[]>();
  for (const [name, color] of entries) {
    const group = colorGroups.get(color);
    if (group) {
      group.push(name);
    } else {
      colorGroups.set(color, [name]);
    }
  }

  const flat: (string | string[])[] = [];
  for (const [color, names] of colorGroups) {
    flat.push(names.length === 1 ? names[0] : names, color);
  }

  return [
    'match',
    ['get', 'name'],
    ...flat,
    defaultColor,
  ] as unknown as Expression;
}

// ─── Component ───

interface TcDevelopmentAreasLayerProps {
  typeCurveGeometry?: GeoJSON.Geometry | null;
  typeCurveEwkb?: string | null;
  typeCurveHeaderDetailsId: number;
}

const TcDevelopmentAreasLayer = ({
  typeCurveEwkb,
  typeCurveHeaderDetailsId,
}: TcDevelopmentAreasLayerProps) => {
  const { current: mapRef } = useMap();
  const devAreasSource = useAtomValue(devAreasSourceAtom);
  // Driven by the map's "Type Curve Units" toggle. Hidden via layout rather than
  // by unmounting: this component also owns unit selection and the frame
  // correction effects, which must keep running while the layers are hidden.
  const unitsVisible = useAtomValue(typeCurveUnitsVisibilityAtom);
  const unitsLayout = useMemo(
    () => ({
      visibility: unitsVisible ? ('visible' as const) : ('none' as const),
    }),
    [unitsVisible]
  );
  const hasNoArea = useAtomValue(tcHasTypeCurveAreaAtom) === false;
  // Inventory Workspace only: hold the units TileJSON (and its downstream vector
  // tiles) until the FOUCS exclusions recompute has returned 200. `null` on plain
  // Type Curve pages, so `foucsBlocked` is false and this layer is unchanged.
  const foucsGate = useAtomValue(tcInventoryFoucsGateAtom);
  const foucsBlocked = foucsGate !== null && foucsGate !== 'ready';
  // Inventory Workspace only: scope the tiles to the workspace + selected formation
  // (mirrors the dev-areas listing call). Both null on plain Type Curve pages.
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  // This layer renders inside both the Main Page map and the Unit Builder map, so
  // its viewport/filter/AOI/working-ring reads must follow ITS surface — else a
  // builder vertex drag would move the main map's cross-section line, etc.
  const surface = useSurface();
  const highlightedFormation = useAtomValue(tcHighlightedFormationAtom);
  const mapFilterMode = useAtomValue(effectiveMapFilterModeAtoms[surface]);
  const mapBounds = useAtomValue(mapBoundsAtoms[surface]);
  const aoiPolygon = useAtomValue(aoiPolygonAtoms[surface]);
  const gesturesSuspended = useAtomValue(mapGesturesSuspendedAtoms[surface]);
  const aoiDrawing = useAtomValue(aoiDrawingAtoms[surface]);
  const workingRing = useAtomValue(unitShapeWorkingRingAtoms[surface]);
  const unitFilters = useAtomValue(tcEnabledUnitFiltersAtom);
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const rawSetSelectedDevArea = useSetAtom(tcSelectedDevAreaAtom);
  // The shared selection (`tcSelectedDevAreaAtom`) has ONE writer — the MAIN
  // surface. Both maps are mounted at once, so if the Unit Builder map's
  // dev-areas layer also wrote it, the two live layers would ping-pong the
  // selection (each pushing crossSection/azimuth derived from its OWN map's tiles
  // + working ring on every `idle`/`sourcedata`), churning the unit the builder's
  // boundary fetch and shape-edit seed depend on. On the builder surface the
  // setter is a no-op; the layer still RENDERS the selection (reads +
  // local frame state) exactly as before.
  const setSelectedDevArea = useMemo(() => {
    return surface === 'main'
      ? rawSetSelectedDevArea
      : ((() => {}) as typeof rawSetSelectedDevArea);
  }, [surface, rawSetSelectedDevArea]);
  // The builder surface publishes its edited section-line here (rather than the
  // shared selection) so its OWN gun barrel + slot table re-fetch as the boundary
  // is reshaped, without touching the main page. See `commitEndpoints`.
  const setBuilderSectionCross = useSetAtom(builderSectionCrossAtom);

  const [selectRequest, setSelectRequest] = useAtom(tcSelectDevAreaRequestAtom);
  const filteredUnits = useAtomValue(tcFilteredUnitsAtom);
  const overlappingUnits = useAtomValue(tcOverlappingUnitsAtom);
  const removedUnitIds = useAtomValue(removedUnitIdsAtom);
  const setbackZoneId = useAtomValue(tcSetbackZoneIdAtom);
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);
  // Kept current so the map click handler can read them without re-registering.
  const overlappingRef = useRef(overlappingUnits);
  overlappingRef.current = overlappingUnits;
  const activeWorkspaceRef = useRef(activeWorkspace);
  activeWorkspaceRef.current = activeWorkspace;
  const unitAzimuthOverrides = useAtomValue(unitAzimuthOverridesAtom);
  const unitNameColorMap = useAtomValue(tcUnitNameColorMapAtom);
  const unitResolutionRefresh = useAtomValue(tcUnitResolutionRefreshAtom);
  // Either inventory action (Generate LTD / Update Geometries) re-lays slots, so
  // both surfaces' dev-area MVT tiles must re-request (each map has its own tile
  // source, so a shared bump re-requests both).
  const geometryRefresh = useAtomValue(geometryRefreshAtom);

  const selectedDevAreaRef = useRef(selectedDevArea);
  selectedDevAreaRef.current = selectedDevArea;

  // Client-only cloned units have no tile geometry, so a select request for one
  // is resolved from its stored snapshot rather than the tiles. Read through a
  // ref so the request effect stays keyed only on the request itself.
  const clonedUnits = useAtomValue(clonedUnitsAtom);
  const clonedUnitsRef = useRef(clonedUnits);
  clonedUnitsRef.current = clonedUnits;

  // Axis frame & drag state (kept local for smooth dragging)
  const frameRef = useRef<DevAreaFrame | null>(null);
  const [frameVersion, setFrameVersion] = useState(0);
  const [sectionT, setSectionT] = useState(0.5);
  const sectionTRef = useRef(sectionT);
  sectionTRef.current = sectionT;

  // Story 3 — current rotation (deg) of the selected unit's section line.
  // Read by the drag callbacks (which use refs, not state) so projection and
  // commit stay on the same rotated axis the line is drawn along. Kept in sync
  // below, once `selectedRotationDeg` is derived.
  const rotationDegRef = useRef(0);
  const isDraggingRef = useRef(false);
  const [labelPosition, setLabelPosition] = useState<{
    lat: number;
    lon: number;
  } | null>(null);

  // ─── Cache unit bounds + frames from tiles so off-screen units remain navigable ───

  const unitBoundsCache = useRef<Map<string, CachedUnit>>(new Map());

  // ─── Fetch the tile URL template from TileJSON ───

  const [tileTemplate, setTileTemplate] = useState<string | null>(null);

  useEffect(() => {
    // Empty state: no Type Curve Area — skip the units TileJSON fetch (and
    // therefore the tile URL / vector-tile requests). Clear any stale template.
    if (hasNoArea) {
      setTileTemplate(null);
      return;
    }
    // Inventory Workspace: wait for the FOUCS exclusions recompute (200) before
    // fetching. Clear any stale template so nothing renders while blocked.
    if (foucsBlocked) {
      setTileTemplate(null);
      return;
    }
    if (!devAreasSource) return;
    const abortController = new AbortController();

    request
      .get<{ tiles?: string[] }>(devAreasSource, abortController)
      .then((res) => {
        if (abortController.signal.aborted) return;
        if ('error' in res) return;
        const tiles = res?.tiles;
        if (Array.isArray(tiles) && tiles.length > 0) {
          setTileTemplate(tiles[0]);
        }
      })
      .catch(() => {
        // Silently ignore failed/aborted requests
      });

    return () => {
      abortController.abort();
    };
  }, [devAreasSource, hasNoArea, foucsBlocked]);

  // ─── Build tile URL with spatial + unit filters ───

  const filterParams = useMemo(
    () =>
      buildDevAreasTileFilterParams(unitFilters, {
        typeCurveHeaderDetailsId,
      }),
    [unitFilters, typeCurveHeaderDetailsId]
  );

  const tileUrl = useMemo(() => {
    if (!tileTemplate) return null;

    // While the user is drawing an AOI (mode selected but no polygon drawn
    // yet), skip the dev-areas tiles entirely. In this state the request would
    // carry no spatial filter — i.e. every development area (heavy, and it can
    // 503) — and, more importantly, a slow/never-settling vector source keeps
    // `map.loaded()` false, which blocks the AOI draw control from ever
    // rendering its layers. Once a polygon exists we request the filtered set.
    // if (mapFilterMode === 'aoi-polygon' && !aoiPolygon) return null;

    const params = new URLSearchParams();

    // Spatial filter — matches build-tc-wells-query-payload.ts
    switch (mapFilterMode) {
      case 'type-curve-area':
        if (typeCurveEwkb) {
          params.set('polygon_ewkb', typeCurveEwkb);
        }
        break;
      case 'visible-extent':
        if (mapBounds) {
          params.set('bounding_box[sw_lng]', String(mapBounds.sw_lng));
          params.set('bounding_box[sw_lat]', String(mapBounds.sw_lat));
          params.set('bounding_box[ne_lng]', String(mapBounds.ne_lng));
          params.set('bounding_box[ne_lat]', String(mapBounds.ne_lat));
        }
        break;
      case 'aoi-polygon':
        if (aoiPolygon) {
          params.set(
            'polygon_geojson',
            JSON.stringify({
              type: aoiPolygon.type,
              coordinates: aoiPolygon.coordinates,
            })
          );
        }
        break;
    }

    // Unit filters
    if (filterParams) {
      for (const [k, v] of new URLSearchParams(filterParams)) {
        params.append(k, v);
      }
    }

    // Inventory Workspace: scope tiles to the workspace's included/excluded units
    // and the selected formation (mirrors buildDevelopmentAreasPayload). Both null
    // on plain Type Curve pages, so those tiles are unchanged. formation_name only
    // takes effect server-side when inventory_workspace_id is also present.
    if (inventoryScope?.inventoryWorkspaceId) {
      params.set(
        'inventory_workspace_id',
        String(inventoryScope.inventoryWorkspaceId)
      );
    }
    if (highlightedFormation) {
      params.set('formation_name', highlightedFormation);
    }

    // The zone whose setbacks measure the outline. Snake_case on purpose: the
    // request-key transform underscores JSON bodies only, so a tile query param
    // reaches Rails exactly as written.
    if (setbackZoneId) {
      params.set('inventory_formation_package_id', String(setbackZoneId));
    }

    // Cache-bust tiles when unit actions change (delete/undo/keep) OR when either
    // inventory action re-lays slots, so the server re-evaluates against the
    // current state and both maps re-request.
    const cacheBust = unitResolutionRefresh + geometryRefresh;
    if (cacheBust > 0) {
      params.set('_v', String(cacheBust));
    }

    const qs = params.toString();
    if (!qs) return tileTemplate;

    const separator = tileTemplate.includes('?') ? '&' : '?';
    return `${tileTemplate}${separator}${qs}`;
  }, [
    tileTemplate,
    mapFilterMode,
    typeCurveEwkb,
    mapBounds,
    aoiPolygon,
    filterParams,
    inventoryScope,
    highlightedFormation,
    setbackZoneId,
    unitResolutionRefresh,
    geometryRefresh,
  ]);

  // ─── Cache all unit centroids from tiles for off-screen navigation ───

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !tileUrl) return;

    const buildCentroidCache = () => {
      // The map can be torn down between this effect being scheduled and run —
      // the Unit Builder unmounts the page's map while it is open. Querying a
      // source that has gone throws inside mapbox-gl ("reading 'version'"), and
      // the layer is inside the route's error boundary, so it blanks the page.
      if (!map.getSource('tc-development-areas')) return;

      const centroids = map.querySourceFeatures('tc-development-areas', {
        sourceLayer: 'development-areas',
        filter: ['==', 'type', 'centroid'],
      });
      if (centroids.length === 0) return;

      for (const f of centroids) {
        const props = f.properties ?? {};
        const name = props.name as string | undefined;
        if (!name) continue;
        const geom = f.geometry;
        if (geom.type !== 'Point') continue;
        const [lon, lat] = geom.coordinates;

        const azimuthDeg = Number(props.azimuth);
        const lengthFt = Number(props.length);
        const widthFt = Number(props.width);
        if (
          !Number.isFinite(azimuthDeg) ||
          !Number.isFinite(lengthFt) ||
          !Number.isFinite(widthFt)
        )
          continue;

        const existing = unitBoundsCache.current.get(name);
        if (existing) {
          // Refresh tile-derived flags — centroid_in_type_curve changes when tileUrl
          // changes (e.g. workspace added/removed from filter params).
          existing.props.centroid_in_type_curve =
            !!props.centroid_in_type_curve;
          // A re-requested tile can bake a different bearing or extent (a rotate
          // or a geometry edit has been cache-busted in). Rebuild the frame then,
          // so the section line follows the tile rather than the first fetch
          // forever; an unchanged unit keeps its frame object, which is what the
          // frame-correction effect keys on.
          const unchanged =
            existing.props.azimuth === azimuthDeg &&
            existing.props.length === lengthFt &&
            existing.props.width === widthFt;
          if (unchanged) continue;
        }

        const frame = computeFrameFromUnitData(
          lat,
          lon,
          azimuthDeg,
          lengthFt,
          widthFt
        );
        const bounds = buildBoundsFromUnitData(
          lat,
          lon,
          azimuthDeg,
          lengthFt,
          widthFt
        );

        unitBoundsCache.current.set(name, {
          bounds,
          props: {
            azimuth: azimuthDeg,
            length: lengthFt,
            width: widthFt,
            development_area_id: Number(props.development_area_id),
            centroid_in_type_curve: !!props.centroid_in_type_curve,
          },
          frame,
        });
      }
    };

    const onSourceData = (e: mapboxGl.MapSourceDataEvent) => {
      if (e.sourceId === 'tc-development-areas' && e.isSourceLoaded) {
        buildCentroidCache();
      }
    };

    const onIdle = () => {
      buildCentroidCache();
    };

    map.on('sourcedata', onSourceData);
    map.on('idle', onIdle);

    // Initial attempt in case tiles are already loaded
    buildCentroidCache();

    return () => {
      map.off('sourcedata', onSourceData);
      map.off('idle', onIdle);
    };
  }, [mapRef, tileUrl]);

  // ─── Select a development area ───

  const selectDevArea = useCallback(
    (name: string) => {
      const map = mapRef?.getMap();
      // Same teardown guard as buildCentroidCache: querying a removed source
      // throws inside mapbox-gl.
      if (!map || !map.getSource('tc-development-areas')) return;

      // Try loaded tiles first, fall back to cache for off-screen units
      const shapeFeatures = map.querySourceFeatures('tc-development-areas', {
        sourceLayer: 'development-areas',
        filter: ['all', ['==', 'name', name], ['==', 'type', 'shape']],
      });

      let bounds: mapboxGl.LngLatBounds;
      let devAreaId: number;
      let cachedFrame: DevAreaFrame | null = null;

      // Always check the cache for centroid-derived metadata (azimuth, center, extents)
      const cached = unitBoundsCache.current.get(name);

      // Prefer cached bounds (computed from centroid + dimensions = full unit)
      // over shape features which may be tile-clipped and incomplete.
      if (cached) {
        bounds = cached.bounds;
      } else if (shapeFeatures.length > 0) {
        bounds = computeBoundsFromFeatures(shapeFeatures);
      } else {
        // Unit not yet in cache — set selection and wait for tiles to load then retry
        const unit = filteredUnits.find((u) => u.name === name);
        setSelectedDevArea({
          developmentAreaId: unit?.id ?? 0,
          developmentAreaName: name,
        });
        const retryOnIdle = () => {
          const cached = unitBoundsCache.current.get(name);
          if (cached) {
            map.off('idle', retryOnIdle);
            selectDevArea(name);
          }
        };
        map.on('idle', retryOnIdle);
        return;
      }

      if (bounds.isEmpty()) return;

      map.fitBounds(bounds, { padding: 40, maxZoom: 14 });

      // Label at top-center of the polygon bounds (outside the unit)
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      setLabelPosition({
        lat: ne.lat,
        lon: (sw.lng + ne.lng) / 2,
      });

      // Get devAreaId from cache or shape feature props
      if (cached) {
        devAreaId = Number(cached.props.development_area_id) || 0;
        cachedFrame = cached.frame;
      } else {
        const shapeProps = shapeFeatures[0]?.properties ?? {};
        devAreaId = Number(shapeProps.development_area_id) || 0;
      }

      const selection: TcSelectedDevArea = {
        developmentAreaId: devAreaId,
        developmentAreaName: name,
        centroidInTypeCurve: cached
          ? !!cached.props.centroid_in_type_curve
          : !!shapeFeatures[0]?.properties?.centroid_in_type_curve,
      };

      // Frame priority: cached frame (from centroid API data) > shape-derived frame
      let frame: DevAreaFrame | null = null;
      if (cachedFrame) {
        frame = cachedFrame;
      } else if (shapeFeatures.length > 0) {
        // Fallback: compute from shape features if cache miss
        const shapeProps = shapeFeatures[0]?.properties ?? {};
        const azimuthDeg = Number(shapeProps.azimuth);
        if (Number.isFinite(azimuthDeg)) {
          frame = computeAxisFrameFromFeatures(
            shapeFeatures,
            (azimuthDeg * Math.PI) / 180
          );
        }
      }

      if (frame) {
        frameRef.current = frame;
        setFrameVersion((v) => v + 1);
        setSectionT(0.5);
        selection.azimuthDeg = (frame.azimuthRad * 180) / Math.PI;
        selection.ring = buildRingFromFrame(frame);

        const { start, end } = sectionLineAtT(frame, 0.5);
        selection.crossSectionEndPoints = {
          start: { lat: start.lat, lon: start.lon },
          end: { lat: end.lat, lon: end.lon },
        };
      } else {
        frameRef.current = null;
        setFrameVersion((v) => v + 1);
      }

      setSelectedDevArea(selection);
    },
    [mapRef, setSelectedDevArea, filteredUnits]
  );

  // ─── Effective frame ───
  // While the shape editor is running, the section line is drawn from the live
  // boundary rather than the shape captured at selection. Dragging has to use
  // that same frame — its [uMin, uMax] is what `t` is measured against, so a
  // stale frame would make the line jump away from the cursor.

  const workingRingRef = useRef<GeoJSON.Position[] | null>(workingRing);
  workingRingRef.current = workingRing;

  const currentFrame = useCallback((): DevAreaFrame | null => {
    const base = frameRef.current;
    const ring = workingRingRef.current;
    if (!ring) return base;
    // No click-derived frame (the Unit Builder mounts a fresh layer where
    // selectDevArea never ran) → fall back to the selection's azimuth so the
    // cross-section line follows the unit's true bearing instead of drawing
    // axis-aligned at 0°.
    const selAz = selectedDevAreaRef.current?.azimuthDeg;
    // Last resort, for a unit built from scratch: it has no selection, so no
    // click-derived frame and no `azimuthDeg` to inherit. Deriving the bearing
    // from the boundary keeps the line along the unit's long axis instead of
    // drawing it flat east-west across a section that runs at 165°.
    const azimuthRad =
      base?.azimuthRad ??
      (typeof selAz === 'number'
        ? (selAz * Math.PI) / 180
        : (ringAxisAzimuthRad(ring) ?? 0));
    return computeAxisFrameFromRing(ring, azimuthRad) ?? base;
  }, []);

  // ─── Commit current t to the shared atom ───

  const commitEndpoints = useCallback(() => {
    const frame = currentFrame();
    const current = selectedDevAreaRef.current;
    if (!frame || !current) return;

    const drawFrame = rotatedFrame(frame, rotationDegRef.current);
    const { start, end } = sectionLineAtT(drawFrame, sectionTRef.current);
    const crossSectionEndPoints = {
      start: { lat: start.lat, lon: start.lon },
      end: { lat: end.lat, lon: end.lon },
    };
    // The Unit Builder edits its OWN section line (from the live boundary) into a
    // builder-scoped atom, so its gun barrel + slot table re-fetch for the new
    // section without writing the shared selection (which the main surface owns).
    // The main surface commits to the shared selection as before.
    if (surface === 'builder') {
      setBuilderSectionCross(crossSectionEndPoints);
    } else {
      setSelectedDevArea({ ...current, crossSectionEndPoints });
    }
  }, [surface, setBuilderSectionCross, setSelectedDevArea, currentFrame]);

  // ─── Drag logic (shared by line layer + arrow marker) ───

  const startDrag = useCallback(
    (e?: mapboxGl.MapLayerMouseEvent) => {
      const map = mapRef?.getMap();
      if (!map || !frameRef.current) return;
      // Only the AOI draw tool takes the gesture away — MapboxDraw owns every
      // map click while it's up. The shape editor doesn't: its handles are
      // their own drag targets, so the section line stays grabbable while
      // reshaping, as it is in the mock.
      if (aoiDrawing) return;

      e?.preventDefault();
      isDraggingRef.current = true;
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';

      const onMove = (mv: mapboxGl.MapMouseEvent) => {
        const frame = currentFrame();
        if (!frame) return;
        const drawFrame = rotatedFrame(frame, rotationDegRef.current);
        setSectionT(projectToT(drawFrame, mv.lngLat.lat, mv.lngLat.lng));
      };

      const onUp = () => {
        map.dragPan.enable();
        map.getCanvas().style.cursor = '';
        map.off('mousemove', onMove);
        map.off('mouseup', onUp);

        commitEndpoints();

        // Let the pending click event pass before clearing the flag
        requestAnimationFrame(() => {
          isDraggingRef.current = false;
        });
      };

      map.on('mousemove', onMove);
      map.on('mouseup', onUp);
    },
    [mapRef, commitEndpoints, currentFrame, aoiDrawing]
  );

  // ─── Map click handler (dev area polygons) ───
  // Uses a short delay so a rapid double-click does not deselect.

  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    // While the AOI draw tool or the unit shape editor is active, it owns map
    // clicks. The unit click handler fires independently on every click and runs
    // selectDevArea → map.fitBounds, which jumps the camera mid-gesture and makes
    // drawing/reshaping unusable (the map-query page works because it has no such
    // layer). Skip registering the unit handlers here; they re-register once the
    // AOI polygon is drawn + locked, the filter mode changes, or editing ends.
    if (gesturesSuspended) return;

    const onClick = (e: mapboxGl.MapLayerMouseEvent) => {
      if (isDraggingRef.current) return;

      // Overlapping units stack with their partners, so the base layer's
      // topmost feature at the click point is often the non-overlapping
      // neighbor. Query every unit feature under the cursor and — inside a
      // workspace — prefer an overlapping (red) one so the user can reliably
      // click it to resolve. Outside a workspace, keep the topmost pick.
      const feats = map.queryRenderedFeatures(e.point, {
        layers: [
          'tc-dev-area-overlap-fill',
          'tc-dev-area-colored-fill',
          'tc-dev-area-fill',
        ],
      });
      if (feats.length === 0) return;

      let picked = feats[0];
      if (activeWorkspaceRef.current) {
        const overlapNames = new Set(overlappingRef.current.map((o) => o.name));
        picked =
          feats.find((f) => overlapNames.has(f.properties?.name as string)) ??
          feats[0];
      }

      const name = picked.properties?.name as string | undefined;
      if (!name) return;

      // Cancel any pending click to absorb double-clicks
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        const isDeselecting =
          selectedDevAreaRef.current?.developmentAreaName === name;
        const isOverlapping = overlappingRef.current.some(
          (o) => o.name === name
        );
        const isWorkspaceOverlap =
          !!activeWorkspaceRef.current && isOverlapping;

        if (isDeselecting && !isWorkspaceOverlap) {
          // Normal toggle-off: clicking the selected unit again deselects it.
          frameRef.current = null;
          setFrameVersion((v) => v + 1);
          setSectionT(0.5);
          setLabelPosition(null);
          setSelectedDevArea(null);
        } else {
          // (Re)select the clicked unit and, for an overlapping unit in a
          // workspace, (re)open the resolution drawer — even when it's already
          // selected. Re-running selectDevArea re-establishes the selection so
          // reopening by clicking the same unit shows its correct conflict
          // group, and it no longer deselects (Story 3).
          selectDevArea(name);
          if (isWorkspaceOverlap) {
            setRightPanel({ type: 'unit-resolution', open: true });
          }
        }
      }, 300);
    };

    const onDblClick = () => {
      // Cancel the pending single-click so selection is not toggled
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
    };

    const onMouseEnter = () => {
      if (!isDraggingRef.current) map.getCanvas().style.cursor = 'pointer';
    };
    const onMouseLeave = () => {
      if (!isDraggingRef.current) map.getCanvas().style.cursor = '';
    };

    const interactiveLayers = ['tc-dev-area-fill', 'tc-dev-area-colored-fill'];
    for (const layerId of interactiveLayers) {
      map.on('click', layerId, onClick);
      map.on('dblclick', layerId, onDblClick);
      map.on('mouseenter', layerId, onMouseEnter);
      map.on('mouseleave', layerId, onMouseLeave);
    }

    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      for (const layerId of interactiveLayers) {
        map.off('click', layerId, onClick);
        map.off('dblclick', layerId, onDblClick);
        map.off('mouseenter', layerId, onMouseEnter);
        map.off('mouseleave', layerId, onMouseLeave);
      }
    };
  }, [
    mapRef,
    selectDevArea,
    setSelectedDevArea,
    setRightPanel,
    gesturesSuspended,
  ]);

  // ─── Section line layer drag + hover cursor ───

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    const onMouseDown = (e: mapboxGl.MapLayerMouseEvent) => startDrag(e);
    const onEnter = () => {
      if (!isDraggingRef.current) map.getCanvas().style.cursor = 'grab';
    };
    const onLeave = () => {
      if (!isDraggingRef.current) map.getCanvas().style.cursor = '';
    };

    map.on('mousedown', 'tc-section-line-layer', onMouseDown);
    map.on('mouseenter', 'tc-section-line-layer', onEnter);
    map.on('mouseleave', 'tc-section-line-layer', onLeave);

    return () => {
      map.off('mousedown', 'tc-section-line-layer', onMouseDown);
      map.off('mouseenter', 'tc-section-line-layer', onEnter);
      map.off('mouseleave', 'tc-section-line-layer', onLeave);
    };
  }, [mapRef, startDrag]);

  // Arrow marker drag (DOM event → map drag pipeline)
  const onMarkerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      startDrag();
    },
    [startDrag]
  );

  // ─── Clear selection when the selected unit is removed (Story 3) ───
  // Removing a unit hides its polygon via the `removedUnitIdsAtom` filter, but
  // the cross-section line is driven by the selection — drop the selection too
  // so the line doesn't linger at the removed unit's old position. Covers every
  // removal path (toolbar bin, drawer single-remove, "Keep" bulk-remove) since
  // they all land in `removedUnitIds`.
  useEffect(() => {
    const id = selectedDevArea?.developmentAreaId;
    if (id === undefined || id === null) return;
    if (!removedUnitIds.has(id)) return;
    frameRef.current = null;
    setFrameVersion((v) => v + 1);
    setSectionT(0.5);
    setLabelPosition(null);
    setSelectedDevArea(null);
  }, [removedUnitIds, selectedDevArea?.developmentAreaId, setSelectedDevArea]);

  // ─── External navigation request ───

  useEffect(() => {
    if (!selectRequest) return;
    // A cloned unit resolves from its snapshot — the tiles carry no geometry for
    // it. (Survey-row restore lives in the Unit Builder, keyed on the selection.)
    const clone = clonedUnitsRef.current.find((c) => c.name === selectRequest);
    if (clone) {
      setSelectedDevArea(clone.selection);
      setSelectRequest(null);
      return;
    }
    selectDevArea(selectRequest);
    setSelectRequest(null);
  }, [selectRequest, selectDevArea, setSelectRequest, setSelectedDevArea]);

  // ─── Correct frame when centroid data arrives for the selected unit ───
  // If the unit was selected before its centroid tile loaded, the frame was
  // computed from tile-clipped shape features (wrong center/extents). Once
  // the centroid cache is populated with accurate API data, swap in the
  // correct frame so the cross-section line is positioned properly.

  useEffect(() => {
    const map = mapRef?.getMap();
    const name = selectedDevArea?.developmentAreaName;
    if (!map || !name || !tileUrl) return;

    const correctFrame = () => {
      const cached = unitBoundsCache.current.get(name);
      if (!cached?.frame) return;

      // Keep centroidInTypeCurve in sync with the cache — covers edge cases
      // where selectDevArea ran before the cache was updated by new tiles.
      const cachedInTc = !!cached.props.centroid_in_type_curve;
      setSelectedDevArea((prev) => {
        if (prev && prev.centroidInTypeCurve !== cachedInTc) {
          return { ...prev, centroidInTypeCurve: cachedInTc };
        }
        return prev;
      });

      // Already using the cached frame — nothing to do
      if (frameRef.current === cached.frame) return;

      frameRef.current = cached.frame;
      setFrameVersion((v) => v + 1);
      setSectionT(0.5);

      const cachedFrame = cached.frame;
      const { start, end } = sectionLineAtT(cachedFrame, 0.5);
      setSelectedDevArea((prev) => {
        return prev
          ? {
              ...prev,
              azimuthDeg: (cachedFrame.azimuthRad * 180) / Math.PI,
              crossSectionEndPoints: {
                start: { lat: start.lat, lon: start.lon },
                end: { lat: end.lat, lon: end.lon },
              },
            }
          : null;
      });
    };

    const onSourceData = (e: mapboxGl.MapSourceDataEvent) => {
      if (e.sourceId === 'tc-development-areas' && e.isSourceLoaded) {
        correctFrame();
      }
    };

    map.on('sourcedata', onSourceData);
    map.on('idle', correctFrame);

    // Try immediately in case cache is already populated
    correctFrame();

    return () => {
      map.off('sourcedata', onSourceData);
      map.off('idle', correctFrame);
    };
  }, [
    mapRef,
    selectedDevArea?.developmentAreaName,
    tileUrl,
    setSelectedDevArea,
  ]);

  // ─── Derive rendering state from frame + t ───

  const selectedName = selectedDevArea?.developmentAreaName ?? null;

  // Story 3 — rotate override (deg) for the selected unit; rotates the LTD
  // geometry direction (the cross-section line), not the unit polygon.
  // The turn applied to the frame THIS layer built from ITS OWN tiles, so the
  // drawn section follows the bearing the server last stated for the unit
  // (unitAzimuthOverridesAtom) rather than whatever the tile happened to bake
  // when it was fetched. The main page and the Unit Builder hold tiles of
  // different ages — the builder fetches fresh ones on every open — and each
  // resolves its own delta, so the two never disagree, and a cache-bust that
  // bakes the stated bearing turns the delta to 0 by itself. There is no session
  // bookkeeping: the only state is the server's last answer.
  //
  // The baked bearing is the cache entry for the unit; before the cache fills
  // (a builder draft with only a working ring), the selection's own azimuth
  // stands in, since that is what currentFrame falls back to as well.
  const selectedRotationDeg = useMemo(() => {
    const id = selectedDevArea?.developmentAreaId;
    if (id === undefined || id === null) return 0;
    const stated = unitAzimuthOverrides.get(
      azimuthOverrideKey(
        inventoryScope?.inventoryWorkspaceId ?? activeWorkspace?.id,
        id
      )
    );
    if (stated === undefined) return 0;
    const cached = selectedName
      ? unitBoundsCache.current.get(selectedName)?.props.azimuth
      : undefined;
    const baked =
      typeof cached === 'number' && Number.isFinite(cached)
        ? cached
        : selectedDevArea?.azimuthDeg;
    if (typeof baked !== 'number' || !Number.isFinite(baked)) return 0;
    return axisDelta(baked, stated);
    // frameVersion: the cache fills and refreshes from tile loads, which bump
    // it; the ref itself is not reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unitAzimuthOverrides,
    inventoryScope?.inventoryWorkspaceId,
    activeWorkspace?.id,
    selectedDevArea?.developmentAreaId,
    selectedDevArea?.azimuthDeg,
    selectedName,
    frameVersion,
  ]);

  // Mirror into a ref so the drag callbacks project/commit on the same axis.
  rotationDegRef.current = selectedRotationDeg;

  // When the rotation of the *currently selected* unit changes (rotate /
  // un-rotate), the cross-section line moves to a new azimuth — commit the new
  // endpoints (commitEndpoints already uses the rotated frame) so dependent
  // views (gun barrel, slot table) re-fetch for the rotated section. Guarded to
  // the same unit so a unit *switch* — already handled by selectDevArea —
  // doesn't double-commit.
  const prevRotationDegRef = useRef(selectedRotationDeg);
  const prevRotationUnitRef = useRef(
    selectedDevArea?.developmentAreaId ?? null
  );
  useEffect(() => {
    const id = selectedDevArea?.developmentAreaId ?? null;
    const sameUnit = id === prevRotationUnitRef.current;
    const rotationChanged = selectedRotationDeg !== prevRotationDegRef.current;
    prevRotationDegRef.current = selectedRotationDeg;
    prevRotationUnitRef.current = id;
    if (sameUnit && rotationChanged) {
      commitEndpoints();
    }
  }, [
    selectedRotationDeg,
    selectedDevArea?.developmentAreaId,
    commitEndpoints,
  ]);

  // A reshape (vertex drag, midpoint insert, delete) or an Undo re-derives the
  // section line's axis frame from the live boundary, so the line moves on
  // screen — but the endpoints the gun barrel and slot table query are only
  // committed on section-line drag end and on rotation. Without this the two
  // panes keep querying the section of the pre-edit shape.
  //
  // Debounced on the trailing edge: the editor reports every drag move, and each
  // commit re-fetches both panes, so committing per move would fetch per pixel.
  // The first value is skipped — entering edit mode seeds the ring with the
  // geometry the endpoints already describe.
  const seenWorkingRingRef = useRef(false);
  useEffect(() => {
    if (!workingRing) {
      seenWorkingRingRef.current = false;
      return undefined;
    }
    if (!seenWorkingRingRef.current) {
      seenWorkingRingRef.current = true;
      return undefined;
    }
    const timer = window.setTimeout(
      commitEndpoints,
      SHAPE_EDIT_COMMIT_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [workingRing, commitEndpoints]);

  const sectionLineData = useMemo(() => {
    const baseFrame = currentFrame();
    // Normally the line belongs to the SELECTED unit. In the builder the unit
    // being edited need not be a selection at all — one built from scratch has
    // no name to gate on — but it does have a working ring, which is everything
    // the line needs. Rotating it stays disabled until the unit is saved
    // (`useInventoryUnitRotation`), so this only draws the axis, never lets it
    // be turned.
    const hasSubject =
      selectedName !== null || (surface === 'builder' && workingRing !== null);
    if (!baseFrame || !hasSubject) return null;

    const frame = rotatedFrame(baseFrame, selectedRotationDeg);

    const { start, end, midLat, midLon } = sectionLineAtT(frame, sectionT);

    // Arrow points along the long axis, normalised to always face "northward"
    let arrowDeg = (frame.azimuthRad * 180) / Math.PI;
    if (arrowDeg > 90) arrowDeg -= 180;
    if (arrowDeg < -90) arrowDeg += 180;

    const geoJson: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [
          [start.lon, start.lat],
          [end.lon, end.lat],
        ],
      },
    };

    return { geoJson, midLat, midLon, arrowBearingDeg: arrowDeg };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sectionT,
    selectedName,
    frameVersion,
    selectedRotationDeg,
    workingRing,
    selectedDevArea?.azimuthDeg,
  ]);

  // ─── Keep dev area layers below wells layers ───

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !tileUrl) return;

    const DEV_AREA_LAYER_IDS = [
      'tc-dev-area-fill',
      'tc-dev-area-line',
      'tc-dev-area-colored-fill',
      'tc-dev-area-colored-line',
      'tc-dev-area-overlap-fill',
      'tc-dev-area-overlap-line',
      'tc-dev-area-selected-fill',
      'tc-dev-area-selected-line',
    ];

    const OUTLINE_LAYER_ID = 'type-curve-outline-layer';

    const reorderLayers = () => {
      const layers = map.getStyle()?.layers;
      if (!layers) return;

      // Find the last dev area layer, the first wells layer, and the TC-area
      // red outline.
      let lastDevAreaIdx = -1;
      let firstWellsIdx = Infinity;
      let firstWellsLayerId: string | undefined;
      let outlineIdx = -1;

      for (let i = 0; i < layers.length; i++) {
        const id = layers[i].id;
        if (DEV_AREA_LAYER_IDS.includes(id)) {
          lastDevAreaIdx = i;
        }
        if (id === OUTLINE_LAYER_ID) {
          outlineIdx = i;
        }
        if (
          i < firstWellsIdx &&
          (id.startsWith('type-curve-scoped-wells-') ||
            id.startsWith('new-wells-') ||
            id.startsWith('wells-in-area-') ||
            id.startsWith('well-control-'))
        ) {
          firstWellsIdx = i;
          firstWellsLayerId = id;
        }
      }

      if (!firstWellsLayerId) return;

      // Desired stack (bottom → top): dev-area layers · TC-area red outline · wells.
      const ordered =
        lastDevAreaIdx < firstWellsIdx &&
        (outlineIdx === -1 ||
          (outlineIdx > lastDevAreaIdx && outlineIdx < firstWellsIdx));
      if (ordered) return;

      // Dev-area fills/lines below wells…
      for (const id of DEV_AREA_LAYER_IDS) {
        if (map.getLayer(id)) {
          map.moveLayer(id, firstWellsLayerId);
        }
      }
      // …then the red TC-area outline just below wells, which lands it above
      // the dev-area layers (draws over the unit fills, under the wells lines).
      if (map.getLayer(OUTLINE_LAYER_ID)) {
        map.moveLayer(OUTLINE_LAYER_ID, firstWellsLayerId);
      }
    };

    const onSourceData = (e: mapboxGl.MapSourceDataEvent) => {
      if (e.sourceId === 'tc-development-areas' && e.isSourceLoaded) {
        reorderLayers();
      }
    };

    map.on('sourcedata', onSourceData);
    map.on('idle', reorderLayers);

    return () => {
      map.off('sourcedata', onSourceData);
      map.off('idle', reorderLayers);
    };
  }, [mapRef, tileUrl]);

  // ─── Keep section line always on top of all other layers ───

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return;

    // While the AOI draw tool is active, MapboxDraw owns the top layers — don't
    // also force the section line on top, which would fight MapboxDraw and churn
    // the style so draw mode never engages. The shape editor is fine: the line
    // has to stay on top there or it stops being grabbable.
    if (aoiDrawing) return;

    const moveToTop = () => {
      if (!map.getLayer('tc-section-line-layer')) return;
      const layers = map.getStyle()?.layers;
      if (!layers || layers.length === 0) return;
      // Already on top — skip. Calling moveLayer unconditionally marks the style
      // dirty and re-emits 'styledata', which would re-invoke this handler in an
      // infinite loop (perpetual style churn).
      if (layers[layers.length - 1].id === 'tc-section-line-layer') return;
      map.moveLayer('tc-section-line-layer');
    };

    // Move after each render and whenever the style is reloaded
    moveToTop();
    map.on('styledata', moveToTop);
    return () => {
      map.off('styledata', moveToTop);
    };
  }, [mapRef, sectionLineData, aoiDrawing]);

  const overlappingNames = useMemo(
    () => overlappingUnits.map((u) => u.name),
    [overlappingUnits]
  );

  const coloredNames = useMemo(
    () => Object.keys(unitNameColorMap),
    [unitNameColorMap]
  );

  const coloredFillColorExpr = useMemo(
    () => buildUnitColorExpr(unitNameColorMap, '#08bdba'),
    [unitNameColorMap]
  );

  const coloredLineColorExpr = useMemo(
    () => buildUnitColorExpr(unitNameColorMap, '#08bdba'),
    [unitNameColorMap]
  );

  // pulse the red overlap fill (1.0 ↔ 0.45 over 1.4s, ease-in-out) so conflicts are
  // impossible to miss. The animation and its teardown guard live in `useOverlapPulse`,
  // which is its own module so the guard can be tested without mounting this component.
  // Only pulse inside an active workspace — not on the plain details page.
  useOverlapPulse(
    mapRef ?? null,
    Boolean(activeWorkspace) && overlappingNames.length > 0
  );

  // Which surface paints this source. The Unit Builder brings its OWN layer set
  // (`UnitBuilderDevAreaLayers`) — a different composition of the same tiles: no
  // `selected` fill, because `TcUnitShapeEditor` paints the unit being edited; no
  // setback bands, because `TcSetbackRingLayer` draws that unit's live ring; and
  // the edited unit filtered out of what remains. Mounting both sets would paint
  // it twice — the whole reason the builder has its own — and the two sets share
  // layer ids, so only one may be added per map. The source, the tile query, the
  // effects, the section line and the label are shared and stay here.
  const ownsLayers = surface !== 'builder';

  // Stable array reference so react-map-gl doesn't recreate the source
  // (and lose cached tiles) on every re-render.
  const tilesArray = useMemo(() => (tileUrl ? [tileUrl] : null), [tileUrl]);

  if (!tilesArray) {
    return null;
  }

  // Client-side layer filters applied on top of the tile set, composed onto
  // every layer's base filter:
  //  - hide removed units by excluding their dev-area ids (MVT shape features
  //    carry `development_area_id`);
  //  - Inventory Workspace: restrict to the origin's unit-name allowlist, so the
  //    map draws only units common to the origin and the selected type curve.
  const removedIdsArr = [...removedUnitIds];
  const notRemoved: any[] | null =
    removedIdsArr.length > 0
      ? ['!in', 'development_area_id', ...removedIdsArr]
      : null;
  const extraClauses: any[][] = [];
  if (notRemoved) extraClauses.push(notRemoved);
  const exclRemoved = (f: any[]): any[] => {
    return extraClauses.length > 0 ? ['all', f, ...extraClauses] : f;
  };

  const baseShapeFilter: any[] = exclRemoved(['==', 'type', 'shape']);

  // The keep-out band the zone's setbacks take off each unit. Absent from the
  // tile unless a zone was requested, so this filter simply matches nothing then.
  const setbackFilter: any[] = exclRemoved(['==', 'type', 'setback']);

  const selectedFilter: any[] = selectedName
    ? exclRemoved([
        'all',
        ['==', 'name', selectedName],
        ['==', 'type', 'shape'],
      ])
    : NONE_FILTER;

  const overlappingFilter: any[] =
    overlappingNames.length > 0
      ? exclRemoved([
          'all',
          ['in', 'name', ...overlappingNames],
          ['==', 'type', 'shape'],
        ])
      : NONE_FILTER;

  const coloredFilter: any[] =
    coloredNames.length > 0
      ? exclRemoved([
          'all',
          ['in', 'name', ...coloredNames],
          ['==', 'type', 'shape'],
        ])
      : NONE_FILTER;

  return (
    <>
      {ownsLayers ? (
        <Source id="tc-development-areas" type="vector" tiles={tilesArray}>
          {/* Layer 1: Base teal (all units). Fill and stroke are the same teal at
            the mock's weights — a light wash under a slightly firmer outline, so
            surrounding units stay context rather than competing with the unit
            being edited (EditSlotMap.tsx:1388-1395). */}
          <Layer
            id="tc-dev-area-fill"
            layout={unitsLayout}
            type="fill"
            source-layer="development-areas"
            filter={baseShapeFilter}
            paint={{
              'fill-color': '#007d79',
              'fill-opacity': 0.08,
            }}
          />
          <Layer
            id="tc-dev-area-line"
            layout={unitsLayout}
            type="line"
            source-layer="development-areas"
            filter={baseShapeFilter}
            paint={{
              'line-color': '#007d79',
              'line-width': 3,
              'line-opacity': 0.6,
            }}
          />
          {/* Layer 2: Colored units from color-by rules */}
          <Layer
            id="tc-dev-area-colored-fill"
            layout={unitsLayout}
            type="fill"
            source-layer="development-areas"
            filter={coloredFilter}
            paint={{
              'fill-color': coloredFillColorExpr as any,
              'fill-opacity': 0.35,
            }}
          />
          <Layer
            id="tc-dev-area-colored-line"
            layout={unitsLayout}
            type="line"
            source-layer="development-areas"
            filter={coloredFilter}
            paint={{
              'line-color': coloredLineColorExpr as any,
              'line-width': 2,
            }}
          />
          {/* Layer 3: Red overlapping units */}
          <Layer
            id="tc-dev-area-overlap-fill"
            layout={unitsLayout}
            type="fill"
            source-layer="development-areas"
            filter={overlappingFilter}
            paint={{
              'fill-color': '#da1e28',
              'fill-opacity': 1,
            }}
          />
          <Layer
            id="tc-dev-area-overlap-line"
            layout={unitsLayout}
            type="line"
            source-layer="development-areas"
            filter={overlappingFilter}
            paint={{
              'line-color': '#da1e28',
              'line-width': 3,
            }}
          />
          <Layer
            id="tc-dev-area-selected-fill"
            layout={unitsLayout}
            type="fill"
            source-layer="development-areas"
            filter={selectedFilter}
            paint={{
              'fill-color': '#0f62fe',
              'fill-opacity': 0.25,
            }}
          />
          <Layer
            id="tc-dev-area-selected-line"
            layout={unitsLayout}
            type="line"
            source-layer="development-areas"
            filter={selectedFilter}
            paint={{
              'line-color': '#0043ce',
              'line-width': 3,
            }}
          />
          {/* Setback keep-out band: the unit with its drillable area punched out
            as a hole, so one shaded fill reads as the band. Orange at 0.18,
            matching the Mock UI. */}
          <Layer
            id="tc-dev-area-setback-fill"
            layout={unitsLayout}
            type="fill"
            source-layer="development-areas"
            filter={setbackFilter}
            paint={{
              'fill-color': '#ff832b',
              'fill-opacity': 0.18,
            }}
          />
          <Layer
            id="tc-dev-area-setback-line"
            layout={unitsLayout}
            type="line"
            source-layer="development-areas"
            filter={setbackFilter}
            paint={{
              'line-color': '#ff832b',
              'line-width': 1,
              'line-dasharray': [4, 4],
            }}
          />
        </Source>
      ) : (
        // Source only: the builder's layer set rides on it. Note the layers must
        // stay DIRECT children of `<Source>` above — react-map-gl hands each
        // direct child the source id through `cloneElement`, so wrapping them in
        // a fragment detaches every one of them.
        <Source id="tc-development-areas" type="vector" tiles={tilesArray} />
      )}
      {/* The selected unit's name tag, its cross-section line and that line's
          rotate handle all belong to a unit, so "Type Curve Units" takes them
          with it — a name tag and a pink axis floating over a map with no units
          on it is what made the checkbox look half-applied. The line's source
          stays mounted either way: the `moveToTop` effect and the rotate drag
          both look the layer up by id. */}
      {unitsVisible && selectedName && labelPosition && (
        <Marker
          longitude={labelPosition.lon}
          latitude={labelPosition.lat}
          anchor="bottom"
          offset={[0, -8]}
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #08bdba',
              padding: '2px 6px',
              fontSize: 12,
              whiteSpace: 'nowrap',
              color: '#161616',
            }}
          >
            {selectedName}
          </div>
        </Marker>
      )}
      <Source
        id="tc-section-line"
        type="geojson"
        data={
          unitsVisible
            ? sectionLineData?.geoJson || EMPTY_GEOJSON
            : EMPTY_GEOJSON
        }
      >
        <Layer
          id="tc-section-line-layer"
          type="line"
          paint={{
            'line-color': '#ee5396',
            'line-width': 4,
            'line-opacity': 0.9,
          }}
        />
      </Source>
      {unitsVisible && sectionLineData && (
        <Marker
          longitude={sectionLineData.midLon}
          latitude={sectionLineData.midLat}
          anchor="center"
        >
          <div
            onMouseDown={onMarkerMouseDown}
            style={{
              width: 15,
              height: 15,
              transform: `rotate(${sectionLineData.arrowBearingDeg}deg)`,
              cursor: 'grab',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 22 22"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11 2 L20 19 L2 19 Z"
                fill="#ee5396"
                stroke="#d12771"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </Marker>
      )}
    </>
  );
};

export { TcDevelopmentAreasLayer };
